import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ContactButton from '../components/ContactButton';
import { useQuery } from 'react-query';
import { format, parseISO } from 'date-fns';
import { vehicleApi } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { convertToBgn, convertToEur } from '../utils/currency';
import { translateEquipment } from '../utils/equipmentTranslations';
import { extractIdFromSlug } from '../utils/slugify';
import { 
  MapPinIcon, 
  CalendarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  TruckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

const VehicleDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  // Extract vehicle ID from the slug (e.g., "2024-porsche-911-carrera-6949" -> 6949)
  const vehicleId = slug ? extractIdFromSlug(slug) : null;

  const { data: vehicle, isLoading, error } = useQuery(
    ['vehicle', vehicleId],
    () => vehicleApi.getVehicle(vehicleId!),
    {
      enabled: !!vehicleId,
    }
  );

  // Image navigation functions
  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % ((vehicle?.images?.length || 0)));
  };

  const previousImage = () => {
    const length = vehicle?.images?.length || 0;
    setCurrentImageIndex((prev) => (prev - 1 + length) % length);
  };

  // Keyboard navigation for image modal
  useEffect(() => {
    if (!isImageModalOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const imagesLength = vehicle?.images?.length || 0;
      switch (e.key) {
        case 'Escape':
          setIsImageModalOpen(false);
          break;
        case 'ArrowLeft':
          if (imagesLength > 1) previousImage();
          break;
        case 'ArrowRight':
          if (imagesLength > 1) nextImage();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isImageModalOpen, vehicle?.images?.length]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !vehicle) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <TruckIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Автомобилът не е намерен</h3>
          <p className="mt-1 text-sm text-gray-500">Автомобилът, който търсите, не съществува.</p>
          <div className="mt-6">
            <Link to="/vehicles" className="btn-primary">
              Преглед на автомобили
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const hasAuction = vehicle.auction_data;
  const images = vehicle.images || [];
  const currentImage = images[currentImageIndex];

  // Convert auction date from EDT to Sofia timezone
  const getSofiaTime = (dateString: string) => {
    try {
      // The date from backend is naive (no timezone info) but represents EDT time
      // We need to parse it as EDT (UTC-4) and convert to Sofia (UTC+3)
      const date = parseISO(dateString);
      
      // Get browser's timezone offset in minutes
      const browserOffset = new Date().getTimezoneOffset(); // negative if ahead of UTC
      
      // EDT offset from UTC in minutes: -4 hours = -240 minutes
      const edtOffset = -240;
      
      // Sofia offset from UTC in minutes: +3 hours = -180 minutes (negative because of getTimezoneOffset)
      const sofiaOffset = -180;
      
      // Calculate the adjustment needed:
      // 1. Remove browser's interpretation (add back browser offset)
      // 2. Apply EDT offset (subtract EDT offset to get UTC)
      // 3. Apply Sofia offset (add Sofia offset from UTC)
      const adjustment = (browserOffset - edtOffset - sofiaOffset) * 60 * 1000;
      
      const sofiaTime = new Date(date.getTime() + adjustment);
      
      return sofiaTime;
    } catch (error) {
      return null;
    }
  };

  const vehicleSpecs = [
    { label: 'VIN номер', value: vehicle.vin },
    { label: 'Сток номер', value: vehicle.stock_number },
    { label: 'Марка', value: vehicle.make },
    { label: 'Модел', value: vehicle.model },
    { label: 'Година', value: vehicle.year },
    { label: 'Тип каросерия', value: vehicle.body_style },
    { label: 'Двигател', value: vehicle.engine },
    { label: 'Скоростна кутия', value: vehicle.transmission },
    { label: 'Задвижване', value: vehicle.drive_type },
    { label: 'Гориво', value: vehicle.fuel_type },
    { label: 'Пробег', value: vehicle.odometer ? `${vehicle.odometer.toLocaleString()} ${vehicle.odometer_unit || 'км'}` : 'Няма инфо' },
    { label: 'Тип документ', value: vehicle.title_type },
    { label: 'Основни щети', value: vehicle.primary_damage },
    { label: 'Вторични щети', value: vehicle.secondary_damage },
    { label: 'Местоположение', value: vehicle.location },
    { label: 'Провинция', value: vehicle.province },
  ].filter(spec => spec.value);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <Link to="/vehicles" className="hover:text-gray-700 flex items-center">
              <ArrowLeftIcon className="w-4 h-4 mr-1" />
              Обратно към автомобили
            </Link>
            <span>/</span>
            <span className="text-gray-900">
              {vehicle.year} {vehicle.make} {vehicle.model}
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Images Section */}
          <div className="space-y-4">
            {images.length > 0 ? (
              <>
                {/* Main Image */}
                <div className="relative bg-gray-200 rounded-lg overflow-hidden aspect-video">
                  <img
                    src={currentImage?.image_url}
                    alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                    className="w-full h-full object-cover cursor-pointer hover:opacity-95 transition-opacity"
                    onClick={() => setIsImageModalOpen(true)}
                  />
                  
                  {/* Navigation buttons */}
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={previousImage}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75"
                      >
                        <ChevronLeftIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75"
                      >
                        <ChevronRightIcon className="w-5 h-5" />
                      </button>
                    </>
                  )}

                  {/* Image counter */}
                  <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-sm">
                    {currentImageIndex + 1} / {images.length}
                  </div>
                </div>

                {/* Thumbnail Gallery */}
                {images.length > 1 && (
                  <div className="grid grid-cols-6 gap-2">
                    {images.map((image, index) => (
                      <button
                        key={image.id}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`aspect-video rounded overflow-hidden ${
                          index === currentImageIndex ? 'ring-2 ring-primary-500' : ''
                        }`}
                      >
                        <img
                          src={image.image_url}
                          alt={`Изглед ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="bg-gray-200 rounded-lg aspect-video flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <TruckIcon className="w-16 h-16 mx-auto mb-2" />
                  <p>Няма налични снимки</p>
                </div>
              </div>
            )}
          </div>

          {/* Vehicle Details */}
          <div className="space-y-6">
            {/* Title and Status */}
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {vehicle.year} {vehicle.make} {vehicle.model}
              </h1>
              <div className="flex flex-wrap gap-2 mb-4">
                {/* Source badge */}
                {vehicle.source && (
                  <span className={`text-sm font-medium px-3 py-1 rounded-full ${
                    vehicle.source === 'IAAI_CANADA' ? 'bg-blue-100 text-blue-800' :
                    vehicle.source === 'IAAI_USA' ? 'bg-purple-100 text-purple-800' :
                    vehicle.source === 'COPART' ? 'bg-orange-100 text-orange-800' :
                    vehicle.source === 'AUTOTRADER_CA' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {vehicle.source === 'IAAI_CANADA' ? 'IAAI Канада 🇨🇦' :
                     vehicle.source === 'IAAI_USA' ? 'IAAI САЩ 🇺🇸' :
                     vehicle.source === 'COPART' ? 'Copart САЩ 🇺🇸' :
                     vehicle.source === 'AUTOTRADER_CA' ? 'Дилърски Канада 🇨🇦' :
                     vehicle.source}
                  </span>
                )}
                {vehicle.is_sold && (
                  <span className="bg-red-100 text-red-800 text-sm font-medium px-3 py-1 rounded-full">
                    Продаден
                  </span>
                )}
                {hasAuction?.is_featured && (
                  <span className="bg-yellow-100 text-yellow-800 text-sm font-medium px-3 py-1 rounded-full">
                    Препоръчан
                  </span>
                )}
                {hasAuction?.is_buy_now && (
                  <span className="bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded-full">
                    Купи сега
                  </span>
                )}
              </div>
            </div>

            {/* Auction Information */}
            {hasAuction && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Информация за търга</h2>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {hasAuction.current_bid && (
                    <div className="flex items-center">
                      <span className="text-lg text-gray-400 mr-2">€</span>
                      <div>
                        <p className="text-sm text-gray-600">Текуща оферта</p>
                        <p className="text-2xl font-bold text-primary-600">
                          {convertToEur(hasAuction.current_bid, vehicle.country).toLocaleString()} €
                          <span className="text-sm font-normal text-gray-500 ml-2">
                            ({convertToBgn(hasAuction.current_bid, vehicle.country).toLocaleString()} лв)
                          </span>
                        </p>
                      </div>
                    </div>
                  )}

                  {hasAuction.buy_now_price && (
                    <div className="flex items-center">
                      <span className="text-lg text-gray-400 mr-2">€</span>
                      <div>
                        <p className="text-sm text-gray-600">Цена "Купи сега"</p>
                        <p className="text-2xl font-bold text-green-600">
                          {convertToEur(hasAuction.buy_now_price, vehicle.country).toLocaleString()} €
                          <span className="text-sm font-normal text-gray-500 ml-2">
                            ({convertToBgn(hasAuction.buy_now_price, vehicle.country).toLocaleString()} лв)
                          </span>
                        </p>
                      </div>
                    </div>
                  )}

                  {hasAuction.auction_date && (
                    <div className="flex items-center">
                      <CalendarIcon className="w-5 h-5 text-gray-400 mr-2" />
                      <div>
                        <p className="text-sm text-gray-600">Дата на търга (Българско време)</p>
                        <p className="font-semibold">
                          {getSofiaTime(hasAuction.auction_date) ? 
                            format(getSofiaTime(hasAuction.auction_date)!, 'dd.MM.yyyy HH:mm')
                            : format(parseISO(hasAuction.auction_date), 'dd.MM.yyyy HH:mm')
                          }
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Contact Button */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <ContactButton 
                      vehicleId={vehicle.id?.toString()} 
                      vehicleName={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                      stockNumber={vehicle.stock_number}
                      variant="primary"
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Vehicle Specifications */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Спецификации на автомобила</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {vehicleSpecs.map((spec) => (
                  <div key={spec.label} className="flex justify-between">
                    <span className="text-gray-600">{spec.label}:</span>
                    <span className="font-medium text-gray-900">{spec.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Extras / Equipment (autotrader listings) */}
            {Array.isArray(vehicle.extra_data?.equipment) && vehicle.extra_data.equipment.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Екстри</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {vehicle.extra_data.equipment.map((item: string) => (
                    <div key={item} className="flex items-center text-sm">
                      <CheckIcon className="w-4 h-4 mr-2 text-green-600 flex-shrink-0" />
                      <span className="text-gray-700">{translateEquipment(item)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Damage Information */}
            {(vehicle.primary_damage || vehicle.secondary_damage) && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <ExclamationTriangleIcon className="w-5 h-5 mr-2 text-yellow-500" />
                  Информация за щетите
                </h2>
                
                <div className="space-y-2">
                  {vehicle.primary_damage && (
                    <div>
                      <span className="text-gray-600">Основни щети:</span>
                      <span className="ml-2 font-medium text-gray-900">{vehicle.primary_damage}</span>
                    </div>
                  )}
                  {vehicle.secondary_damage && (
                    <div>
                      <span className="text-gray-600">Вторични щети:</span>
                      <span className="ml-2 font-medium text-gray-900">{vehicle.secondary_damage}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Location Information */}
            {vehicle.location && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <MapPinIcon className="w-5 h-5 mr-2 text-gray-500" />
                  Местоположение
                </h2>
                
                <p className="text-gray-900">
                  {vehicle.location}
                  {vehicle.province && `, ${vehicle.province}`}
                </p>
              </div>
            )}
            
            {/* Contact Section */}
            <div className="mt-8 p-6 bg-blue-50 rounded-lg">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Интересувате се от този автомобил?
                </h3>
                <p className="text-gray-600 mb-4">
                  Свържете се с нас за повече информация.
                </p>
                <ContactButton 
                  vehicleId={vehicle.id?.toString()} 
                  vehicleName={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                  stockNumber={vehicle.stock_number}
                  variant="primary"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Image Modal */}
        {isImageModalOpen && currentImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-95 p-4"
          onClick={() => setIsImageModalOpen(false)}
        >
          <div className="relative max-w-7xl max-h-full w-full">
            {/* Close button */}
            <button
              onClick={() => setIsImageModalOpen(false)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10 bg-black bg-opacity-50 rounded-full p-2"
              aria-label="Затвори"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Image navigation buttons */}
            {images.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    previousImage();
                  }}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-75 transition-all z-10"
                >
                  <ChevronLeftIcon className="w-8 h-8" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    nextImage();
                  }}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-75 transition-all z-10"
                >
                  <ChevronRightIcon className="w-8 h-8" />
                </button>
              </>
            )}

            {/* Main image */}
            <img
              src={currentImage.image_url}
              alt={`${vehicle.year} ${vehicle.make} ${vehicle.model} - Снимка ${currentImageIndex + 1}`}
              className="max-w-full max-h-[90vh] object-contain mx-auto rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default VehicleDetailPage;
